@if (paymentState() === 'pending') {
  <div class="text-center mb-8">
    <div
      class="w-20 h-20 rounded-full mx-auto mb-5 flex items-center justify-center border-2 payment-pending-icon"
      style="background: rgb(var(--theme-amber-rgb) / 0.12); border-color: var(--theme-amber)"
    >
      <ng-icon size="36" name="lucideTimer" style="color: var(--theme-amber)" />
    </div>
    <h3 class="text-xl sm:text-2xl font-bold text-deep-green mb-2">Awaiting Payment Approval</h3>
    <p class="text-sm text-muted-foreground max-w-md mx-auto">
      Please check your mobile phone and enter your Mobile Money PIN when prompted by your telecom
      provider.
    </p>
  </div>

  <div
    hlmCard
    class="rounded-xl p-6 text-center border border-border/70 bg-slate-50/70 space-y-3"
  >
    @if (isCheckingPayment()) {
      <hlm-spinner class="text-2xl mx-auto" />
      <p class="text-sm font-semibold text-foreground">
        Verifying transaction with network provider...
      </p>
      <p class="text-xs text-muted-foreground">
        Auto-checking status every 10 seconds. Please keep this window open.
      </p>
    } @else {
      <p class="text-sm font-semibold text-foreground">Unable to confirm payment</p>
      <p class="text-xs text-muted-foreground max-w-sm mx-auto">
        If you have already approved the transaction on your phone, click below to verify
        immediately.
      </p>
      <div class="flex flex-col sm:flex-row items-center justify-center gap-2 pt-2">
        <button
          hlmBtn
          type="button"
          variant="outline"
          (click)="retryPaymentStatusCheck()"
          class="rounded-xl font-semibold border-primary/30 text-primary hover:bg-primary/5"
        >
          <ng-icon name="lucideRefreshCw" class="size-4 mr-1.5" />
          Check Status Now
        </button>
        <button
          hlmBtn
          variant="ghost"
          type="button"
          (click)="onBack()"
          class="inline-flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-deep-green cursor-pointer"
        >
          <ng-icon name="lucideArrowLeft" class="size-3.5" />
          Return to payment options
        </button>
      </div>
    }
  </div>
}

@if (paymentState() === 'failed') {
  <div class="text-center mb-8">
    <div
      class="w-20 h-20 rounded-full mx-auto mb-5 flex items-center justify-center border-2 payment-failed-icon"
      style="background: rgb(var(--theme-secondary-rgb) / 0.1); border-color: var(--theme-secondary)"
    >
      <ng-icon size="36" name="lucideTriangleAlert" class="text-destructive" />
    </div>
    <h3 class="text-xl sm:text-2xl font-bold text-deep-green mb-2">Payment Failed</h3>
    <p class="text-sm text-muted-foreground max-w-md mx-auto">
      Unfortunately, your payment could not be processed. Please try again.
    </p>
  </div>

  <div hlmCard class="rounded-xl p-6 mb-6 border border-border/70 bg-slate-50/70 space-y-5">
    <div class="text-center">
      <div
        class="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center bg-destructive/10"
      >
        <ng-icon size="20" name="lucideX" class="text-destructive" />
      </div>
      <h4 class="font-bold text-destructive">Transaction Failed</h4>
      <p class="text-xs text-muted-foreground mt-1">
        Your payment was not verified by the payment provider
      </p>
    </div>

    <hlm-separator class="bg-border" />

    <div class="space-y-4">
      <h4 class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
        What you can do:
      </h4>
      <ul class="space-y-2 text-xs text-muted-foreground">
        <li class="flex items-start gap-2">
          <span class="text-gold font-bold">•</span>
          <span>Ensure your mobile money wallet has sufficient balance for the fee.</span>
        </li>
        <li class="flex items-start gap-2">
          <span class="text-gold font-bold">•</span>
          <span>Check if your phone received the USSD approval pop-up and authorize it promptly.</span>
        </li>
        <li class="flex items-start gap-2">
          <span class="text-gold font-bold">•</span>
          <span>Verify your network connection and retry the payment.</span>
        </li>
      </ul>

      <div class="flex justify-between items-center pt-3 border-t border-border/60">
        <span class="text-sm text-muted-foreground">Serial Number</span>
        <span class="font-bold font-mono text-primary">
          {{ sessionService.formSubData()?.user?.serial_number || 'N/A' }}
        </span>
      </div>
    </div>

    <div class="text-center">
      <button
        hlmBtn
        type="button"
        variant="outline"
        (click)="onBack()"
        class="rounded-xl font-semibold border-primary/30 text-primary hover:bg-primary/5"
      >
        <ng-icon name="lucideRefreshCw" class="size-4 mr-1.5" />
        Try Payment Again
      </button>
    </div>
  </div>
}

@if (paymentState() === 'success') {
  <div class="text-center mb-8">
    <div
      class="w-20 h-20 rounded-full mx-auto mb-5 flex items-center justify-center step-complete shadow-lg"
      style="box-shadow: 0 10px 25px -5px rgb(var(--theme-success-rgb) / 0.35)"
    >
      <ng-icon size="34" name="lucideCheck" class="text-white" />
    </div>
    <h3 class="text-xl sm:text-2xl font-bold text-deep-green mb-2">Payment Confirmed</h3>
    <p class="text-sm text-muted-foreground max-w-md mx-auto">Payment verified successfully!</p>
  </div>

  <div class="rounded-xl p-6 mb-6 border border-border/70 bg-slate-50/70">
    <div class="flex justify-between items-center mb-3 pb-3 border-b border-border/60">
      <span class="text-sm text-muted-foreground">Serial Number</span>
      <span class="font-bold font-mono text-primary">
        {{ sessionService.formSubData()?.user?.serial_number }}
      </span>
    </div>
    <div class="flex justify-between items-center">
      <span class="text-sm text-muted-foreground">Status</span>
      <span
        class="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full"
        style="background: rgb(var(--theme-success-rgb) / 0.12); color: var(--theme-success-dark)"
      >
        <ng-icon size="12" name="lucideCheck" />
        Paid &amp; Verified
      </span>
    </div>
  </div>

  <div class="text-center space-y-3">
    <p class="text-sm text-muted-foreground mb-2">You can now check your application status.</p>
    <button
      hlmBtn
      (click)="checkStatus()"
      type="button"
      class="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-bold text-sm transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
    >
      Check Status
      <ng-icon name="lucideArrowRight" class="size-4" />
    </button>
  </div>
}
